// NPM Imports
import React, { Component, Fragment } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { Tooltip } from "../../../revvComponents";

// Components Imports
import { Dropdown } from "../../../commonComponents";

// Internal Imports
import { GlobalUtils, OppoUtils } from "../../../utils";
import { PATHS, PERMISSIONS, PIPELINE, COMMON } from "../../../constants";

// Destructuring
const {
  DOCUMENT_TYPE_ICONS,
  INTERNAL_REVIEW,
  DRAFT,
  CUSTOMER_REVIEW,
  SIGNED,
  DOCUMENT_STATUS_TOOLTIP_HASMAP,
  DOCUMENT_STATUS_LABEL,
} = PIPELINE;
const { CURRENCY_SYMBOL } = COMMON;
const {
  checkUserPermission,
  readDataFromLocalStorage,
  writeDataToLocalStorage,
} = GlobalUtils;
const {
  decideDeadlineDateClassName,
  decideDeadlineDaysCount,
  getDocStatusClassName,
} = OppoUtils;

class PipelineSwimLaneCard extends Component {
  dropdownRef = React.createRef();

  state = {
    showDeleteDropdown: false,
  };

  shouldComponentUpdate(nextProps, nextState) {
    // The only possible state change here is if the card has to be deleted
    if (nextState.showDeleteDropdown === this.state.showDeleteDropdown) {
      return false;
    }
    return true;
  }

  // toggle "showSortOppsPopup" when click on sort opportunities button
  _toggleDeleteDocumentDropdownHandler = () => {
    // attach/remove event handler
    if (!this.state.showDeleteDropdown) {
      document.addEventListener(
        "click",
        this._handleDeleteDropdownOutsideClick,
        false,
      );
    } else {
      document.removeEventListener(
        "click",
        this._handleDeleteDropdownOutsideClick,
        false,
      );
    }

    this.setState(prevState => ({
      showDeleteDropdown: !prevState.showDeleteDropdown,
    }));
  };

  _handleDeleteDropdownOutsideClick = e => {
    const node = this.dropdownRef.current;
    // ignore clicks on the component itself
    if (node && node.contains(e.target)) {
      return;
    }
    this._toggleDeleteDocumentDropdownHandler();
  };

  // check if the "Document Owner" is a "Current User"
  _isDocOwner = docOwnerID => {
    const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
    return CURRENT_USER ? CURRENT_USER.id === docOwnerID : false;
  };

  // DROPDOWN OPTIONS LIST ::
  // get options list based on "Document Status", "Permissions",
  // & "Document Ownership"
  _getOptionsForDocumentCard = optionsConfig => {
    const {
      context,
      documentId,
      documentNo,
      confirmDeleteDocument,
      hasDeletePermission,
      isDocOwner,
    } = optionsConfig;

    // DOCUMENT DELETE BUTTON OPTION ::
    // push the "deleteOptionConfig" obj in "optionsList" array
    // based on lane, permission and ownership
    const deleteOptionConfig = {
      name: "Delete",
      argument: { documentId, documentNo },
      icon: ["far", "trash-alt"],
      action: confirmDeleteDocument,
    };

    // Dropdown Menu options list which will contain the
    // document actions ex: delete, edit, preview, etc
    const optionsList = [];

    switch (context) {
      case DRAFT:
        // check document delete permission & ownership
        if (hasDeletePermission && isDocOwner) {
          optionsList.push(deleteOptionConfig);
        }
        return optionsList;
      case INTERNAL_REVIEW:
        return optionsList;
      case CUSTOMER_REVIEW:
        return optionsList;
      case SIGNED:
        return optionsList;
      default:
        return optionsList;
    }
  };

  _dropdownOptionsData = optionsConfig => ({
    icon: "ellipsis-v",
    position: "bottom",
    title: "",
    selectedClassNameIdent: "",
    className: "pipelineswimlanecard-deletedropdown-iconwrapper",
    options: this._getOptionsForDocumentCard(optionsConfig),
  });

  // DOCUMENT ACTION TEXT ::
  // show "Edit" if
  //       - document is in a "DRAFT" mode &
  //       - user has "modify all permission" or
  //       - user has "edit permission" and user is a document owner
  // show "View"
  //       - if above conditions don't meet
  _getDocumentActionText = (
    context,
    hasModifyAllPermission,
    hasEditPermission,
    isDocOwner,
  ) => {
    if (
      context === DRAFT &&
      (hasModifyAllPermission || (hasEditPermission && isDocOwner))
    ) {
      return { fileIcon: "file-edit", title: "Edit " };
    }
    return { fileIcon: "eye", title: "View " };
  };

  // Show the "Dropdown Menu" if there are options list
  _decideDocDropdownMenu = optionsData => {
    if (optionsData.options.length > 0) {
      return (
        <Dropdown
          options={optionsData}
          showDropdown={this.state.showDeleteDropdown}
          showDropdownHandler={this._toggleDeleteDocumentDropdownHandler}
          dropdownRef={this.dropdownRef}
        />
      );
    }
    return null;
  };

  _truncateString = (string, length) => {
    if (string && string.length >= length) {
      return `${string.slice(0, length)}...`;
    }
    return string;
  };

  _decideDocumentStatus = docStatus => {
    if (DOCUMENT_STATUS_LABEL[docStatus]) {
      return (
        <Tooltip
          placement="left"
          align={{
            offset: [3, 0],
          }}
          overlay={DOCUMENT_STATUS_TOOLTIP_HASMAP[docStatus]}>
          <span
            className={`pipelineswimlanecard-${getDocStatusClassName(
              docStatus,
            )} pipeline-document-status-label`}>
            {docStatus === "DOCUMENT_CLIENT_SIGNED" ? (
              <FontAwesomeIcon icon={["fas", "check-circle"]} />
            ) : (
              <FontAwesomeIcon icon={["far", "exclamation-triangle"]} />
            )}
          </span>
        </Tooltip>
      );
    }
    return null;
  };

  render() {
    const { context } = this.props.swimlane;
    let cardSpace;
    if (
      context === DRAFT ||
      context === INTERNAL_REVIEW ||
      context === CUSTOMER_REVIEW ||
      context === SIGNED
    ) {
      const {
        id: documentId,
        title: documentName,
        doc_no: documentNo,
        status: docStatus,
        deal_value: netDealValue,
        revision_no: revisionNumber,
        deadline_date: deadlineDate,
        deal_currency: dealCurrency,
        created_by: {
          id: docOwnerID,
          name: docOwnerName,
          image_url: docOwnerProfileImageUrl,
        },
      } = this.props.opportunity.docs;

      const { name: opportunityName } = this.props.opportunity;

      const optionsConfig = {
        context,
        confirmDeleteDocument: this.props.confirmDeleteDocument,
        documentId,
        documentNo,
        hasDeletePermission: checkUserPermission(
          PERMISSIONS.PIPELINE.DELETE_DEALS,
        ),
        isDocOwner: this._isDocOwner(docOwnerID),
      };
      const documentDropdownOptions = this._dropdownOptionsData(optionsConfig);

      // const docActionData = this._getDocumentActionText(
      //   context,
      //   checkUserPermission(PERMISSIONS.PIPELINE.MODIFY_ALL_DEALS),
      //   checkUserPermission(PERMISSIONS.PIPELINE.UPDATE_DEALS),
      //   this._isDocOwner(docOwnerID),
      // );

      const dlDate = GlobalUtils.formatDate(deadlineDate, "lll");
      const currentDate = new Date();
      const deadlineLeftHours = moment(dlDate).diff(
        moment(currentDate),
        "days",
        true,
      );

      cardSpace = (
        <div
          className="pipelineswimlanecard-wrapper"
          data-test={`pipelineswimlanecard-wrapper-${docStatus}`}>
          <div className="pipelineswimlanecard-docstatus-wrapper">
            <div className="pipelineswimlanecard-actions">
              {/* <Link to={`${PATHS.DOCUMENTS_PATH}/${documentId}`} className="pipelineswimlanecard-link">
                <span className="pipelineswimlanecard-actionsitem">
                  <FontAwesomeIcon
                    icon={["far", `${docActionData.fileIcon}`]}
                  />
                  <span className="btn-text">{docActionData.title}</span>
                </span>
              </Link> */}
              {/* ---- Dropdown Menu Section ---- */}
              {this._decideDocumentStatus(docStatus)}
              {this._decideDocDropdownMenu(documentDropdownOptions)}
            </div>
          </div>
          <Link
            to={`${PATHS.DOCUMENTS_PATH}/${documentNo}`}
            onClick={() =>
              writeDataToLocalStorage("documentPageSource", "pipeline")
            }>
            <div
              className="pipelineswimlanecard-doc-headingwrapper"
              data-test={`pipelineswimlanecard-doc-headingwrapper-${docStatus}`}>
              <span
                className="pipelineswimlanecard-doc-fileicon"
                data-test="pipelineswimlanecard-doc-fileicon">
                <img
                  src={DOCUMENT_TYPE_ICONS[documentNo.split("-")[0]]}
                  alt="document-type"
                />
              </span>
              <span className="pipelineswimlanecard-doc-heading">
                <span
                  className="pipelineswimlanecard-doc-headinglink"
                  data-test="pipelineswimlanecard-doc-headinglink">
                  {this._truncateString(documentName, 25)}
                </span>
                <span
                  className="pipelineswimlanecard-doc-subheading"
                  data-test="pipelineswimlanecard-doc-subheading">
                  {documentNo}
                  {revisionNumber > 0 && (
                    <span className="pipelineswimlanecard-document-revision-label">
                      Revision {revisionNumber}
                    </span>
                  )}
                </span>
              </span>
            </div>

            <span
              className="pipelineswimlanecard-sub-heading"
              data-test="pipelineswimlanecard-sub-heading">
              {this._truncateString(opportunityName, 40)}
            </span>
            <div className="pipelineswimlanecard-meta">
              <div className="pipelineswimlanecard-metadata">
                <span
                  className="pipelineswimlanecard-metatitle"
                  data-test="pipelineswimlanecard-metatitle">
                  <span className="ic-meta">
                    <FontAwesomeIcon icon={["far", "calendar-alt"]} />
                  </span>
                  Turn Around Date
                </span>
                {deadlineDate ? (
                  <Tooltip
                    placement="bottom"
                    overlay={decideDeadlineDaysCount(deadlineLeftHours)}
                    align={{
                      offset: [0, -4],
                    }}>
                    <span
                      className={`pipelineswimlanecard-dealline-date ${decideDeadlineDateClassName(
                        deadlineLeftHours,
                      )}`}
                      data-test="pipelineswimlanecard-dealline-date">
                      {GlobalUtils.formatDate(deadlineDate, "ll")}
                    </span>
                  </Tooltip>
                ) : (
                  "-"
                )}
              </div>
              {netDealValue !== 0 && (
                <div>
                  <span className="pipelineswimlanecard-metatitle">
                    <span className="ic-meta">
                      <FontAwesomeIcon icon={["far", "money-bill-wave"]} />
                    </span>
                    Deal Value
                  </span>
                  <span className="pipelineswimlanecard-metatinfo">
                    {CURRENCY_SYMBOL[dealCurrency]}
                    {netDealValue && netDealValue.toFixed(2)}
                  </span>
                </div>
              )}

              <div className="pipelineswimlanecard-pic-wrapper">
                <Tooltip
                  placement="top"
                  align={{
                    offset: [0, 3],
                  }}
                  overlay={docOwnerName || "-"}>
                  <img
                    data-test="pipelineswimlanecard-pic"
                    className="pipelineswimlanecard-pic"
                    src={docOwnerProfileImageUrl}
                    alt="Document Owner Profile"
                    height="20px"
                    width="20px"
                  />
                </Tooltip>
              </div>
            </div>
            {/* {this._decideDocumentStatus(docStatus)} */}
          </Link>
        </div>
      );
    } else if (this.props.error) {
      cardSpace = <span>Error</span>;
    }
    return <Fragment>{cardSpace}</Fragment>;
  }
}

export default PipelineSwimLaneCard;
